import { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import QuestionCard from './QuestionCard';
import { getPricingConfig, calculateFinalPrice } from '../../../services/pricingService';

// الخيارات التي تتعامل كمجموعة راديو (اختيار واحد فقط)
const EDITING_OPTIONS = ['basic-editing', 'professional-editing'];
const DEFAULT_EDITING = 'basic-editing';

/* حساب السعر الكلي مع equipment محدد */
const calcTotal = (pricingConfig, formData, equipment) => {
  if (!pricingConfig) return null;
  try {
    return calculateFinalPrice({ ...formData, equipment }, pricingConfig).total;
  } catch {
    return null;
  }
};

const EquipmentStep = ({ formData, onUpdate, isDirectMode, preselectedPhotographer }) => {
  const initialEquipment = formData?.equipment?.length > 0
    ? formData.equipment
    : [DEFAULT_EDITING];

  const [selectedEquipment, setSelectedEquipment] = useState(initialEquipment);
  const [pricingConfig, setPricingConfig] = useState(null);

  /* جلب إعدادات التسعير مرة واحدة (مخزنة مؤقتاً) */
  useEffect(() => {
    getPricingConfig().then(setPricingConfig).catch(() => {});
  }, []);

  /* مزامنة الافتراضي مع formData عند التحميل الأول فقط */
  useEffect(() => {
    if (!formData?.equipment || formData.equipment.length === 0) {
      onUpdate({
        equipment: [DEFAULT_EDITING],
        equipmentLabels: ['تعديل أساسي (تصحيح إضاءة وألوان)']
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* فرق السعر بين الاحترافي والأساسي — يُحسب مرة واحدة عند توفر الـ config */
  const priceDiff = useMemo(() => {
    if (!pricingConfig) return null;
    const proTotal   = calcTotal(pricingConfig, formData, ['professional-editing']);
    const basicTotal = calcTotal(pricingConfig, formData, ['basic-editing']);
    if (proTotal == null || basicTotal == null) return null;
    return Math.max(0, proTotal - basicTotal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingConfig]);

  /* السعر الإجمالي بناءً على الاختيار الحالي */
  const currentTotal = useMemo(() => {
    return calcTotal(pricingConfig, formData, selectedEquipment);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingConfig, selectedEquipment]);

  /* ملف المصور — للتحقق من توفر التعديل الاحترافي في الحجز المباشر */
  const photographerEquipmentRaw =
    preselectedPhotographer?.equipmentAvailable ||
    preselectedPhotographer?.equipment_available ||
    formData?.directPhotographerProfile?.equipmentAvailable ||
    [];
  const photographerEquipment = Array.isArray(photographerEquipmentRaw)
    ? photographerEquipmentRaw : [];

  const equipmentMatches = (pe, eq) =>
    pe === eq.value || pe === eq.id || pe === eq.label ||
    (typeof pe === 'string' && pe.toLowerCase().includes(eq.value.toLowerCase()));

  const profOption = { id: 'professional-editing', value: 'professional-editing', label: 'تعديل احترافي متقدم' };
  const isProfessionalAvailable =
    !isDirectMode ||
    photographerEquipment.length === 0 ||
    photographerEquipment.some(pe => equipmentMatches(pe, profOption));

  /* اختيار خيار (يعمل كـ radio) */
  const selectOption = (equipmentId) => {
    const nonEditingSelection = selectedEquipment.filter(id => !EDITING_OPTIONS.includes(id));
    if (selectedEquipment.includes(equipmentId)) return; // لا إلغاء
    const newSelection = [...nonEditingSelection, equipmentId];
    setSelectedEquipment(newSelection);
    const labels = {
      'professional-editing': 'تعديل احترافي متقدم',
      'basic-editing': 'تعديل أساسي (تصحيح إضاءة وألوان)',
      'no-editing': 'صور أصلية بدون تعديل',
    };
    onUpdate({
      equipment: newSelection,
      equipmentLabels: newSelection.map(id => labels[id] || id),
    });
  };

  const isYesSelected = selectedEquipment.includes('professional-editing');
  const isNoSelected  = !isYesSelected;

  /* عنوان ووصف ديناميكيان بحسب السياق */
  const cardTitle = isDirectMode
    ? isProfessionalAvailable
      ? 'أضف تعديلاً احترافياً لجلستك؟'
      : 'التعديل المشمول في جلستك'
    : 'هل تريد تعديلاً احترافياً لصورك؟';

  const cardDescription = isDirectMode
    ? isProfessionalAvailable
      ? 'هذا المصور يقدم التعديل الاحترافي — يمكنك إضافته الآن برسوم إضافية'
      : 'هذا المصور يقدم التعديل الأساسي (تصحيح الإضاءة والألوان) مشمولاً مع الجلسة'
    : 'التعديل الأساسي مشمول مجاناً — اختر الاحترافي للحصول على معالجة متقدمة';

  return (
    <QuestionCard
      icon={isDirectMode && !isProfessionalAvailable ? 'Sliders' : 'Sparkles'}
      title={cardTitle}
      description={cardDescription}
    >
      <div className="grid grid-cols-1 gap-3">

        {/* ── لا / التعديل الأساسي ── */}
        <button
          onClick={() => selectOption('basic-editing')}
          className={`p-5 rounded-2xl border-2 text-right transition-all duration-200 ${
            isNoSelected
              ? 'border-slate-400 bg-slate-50 shadow-sm ring-2 ring-slate-200'
              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50 hover:shadow-sm'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 mb-2">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isNoSelected ? 'bg-slate-200' : 'bg-slate-100'
                }`}>
                  <Icon name="Sliders" size={18} color={isNoSelected ? '#475569' : '#94a3b8'} />
                </div>
                <span className="text-base font-semibold text-slate-900 leading-tight">
                  {isDirectMode && !isProfessionalAvailable
                    ? 'تعديل أساسي — تصحيح الإضاءة والألوان'
                    : 'لا شكراً، يكفيني الأساسي'}
                </span>
              </div>
              <p className="text-sm text-slate-500 mb-3 pr-11">
                تصحيح الإضاءة والألوان والوضوح — مشمول مجاناً مع جميع الباقات
              </p>
              <div className="pr-11">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
                  isNoSelected
                    ? 'bg-slate-200 text-slate-600'
                    : 'bg-slate-100 text-slate-500'
                }`}>
                  مشمول مجاناً
                </span>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
              isNoSelected ? 'border-slate-500 bg-slate-500' : 'border-slate-300'
            }`}>
              {isNoSelected && <Icon name="Check" size={13} color="white" />}
            </div>
          </div>
        </button>

        {/* ── نعم / تعديل احترافي (يظهر فقط إذا كان المصور يقدمه في الحجز المباشر) ── */}
        {isProfessionalAvailable && (
          <button
            onClick={() => selectOption('professional-editing')}
            className={`p-5 rounded-2xl border-2 text-right transition-all duration-200 ${
              isYesSelected
                ? 'border-emerald-400 bg-emerald-50/60 shadow-md ring-2 ring-emerald-200'
                : 'border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30 hover:shadow-sm'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isYesSelected ? 'bg-emerald-100' : 'bg-amber-50'
                  }`}>
                    <Icon name="Sparkles" size={18} color={isYesSelected ? '#059669' : '#d97706'} />
                  </div>
                  <span className="text-base font-semibold text-slate-900 leading-tight">
                    {isDirectMode ? 'نعم، أضف التعديل الاحترافي' : 'نعم، أريد تعديلاً احترافياً'}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mb-3 pr-11">
                  تعديل متكامل يشمل التنقية الكاملة ومعالجة الخلفية والتفاصيل الدقيقة
                </p>
                <div className="pr-11">
                  {priceDiff != null ? (
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
                      isYesSelected
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      <Icon name="Plus" size={12} />
                      {priceDiff.toLocaleString('en-US')} ر.س
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                      <span className="w-3 h-3 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin" />
                      جاري الحساب...
                    </span>
                  )}
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
                isYesSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'
              }`}>
                {isYesSelected && <Icon name="Check" size={13} color="white" />}
              </div>
            </div>
          </button>
        )}

      </div>

      {currentTotal != null && (
        <div className="mt-4 flex items-center justify-between px-4 py-3.5 bg-white border-2 border-slate-300 rounded-xl">
          <div>
            <span className="text-sm font-semibold text-slate-700">إجمالي الطلب</span>
            <p className="text-xs text-slate-400 mt-0.5">شامل جميع الرسوم</p>
          </div>
          <span className="text-lg font-bold text-slate-900">
            {currentTotal.toLocaleString('en-US')} ر.س
          </span>
        </div>
      )}
    </QuestionCard>
  );
};

export default EquipmentStep;
